// frontend/src/infrastructure/speech/VoskSpeechEngine.ts
//
// Lokalny (on-device) silnik rozpoznawania mowy oparty o `vosk-browser` (WASM +
// WebWorker). Działa W PEŁNI OFFLINE — po jednorazowym pobraniu modelu żaden
// bajt audio nie opuszcza urządzenia. To zamiennik dla:
//   • Deepgram (chmura, wymaga klucza i sieci) — świadomie NIE używany,
//   • przeglądarkowego `webkitSpeechRecognition` (na Androidzie otwiera systemowe
//     okno „Dyktuj", na desktopie Chrome i tak strzela do Google) — odpada.
//
// Warstwa infrastruktury: cała rozmowa z `getUserMedia` / `AudioContext` /
// `vosk-browser` żyje tutaj. Wszystkie zależności są wstrzykiwalne — w testach
// atrapy, w produkcji realne API. Wzorzec 1:1 jak `VoiceActivityDetector`.
//
// Kontrakt dla warstwy wyżej (Etap 2 — `useSpeechToText`):
//   engine.load()                — pobierz / zainicjuj model (idempotentne),
//   engine.start(callbacks)      — mikrofon + rozpoznawanie; `onPartial` leci na
//                                  żywo, `onFinal` DOKŁADNIE RAZ po `stop()`,
//   engine.stop()                — domknij wypowiedź, wypłucz ostatni wynik,
//   engine.cancel()              — przerwij bez `onFinal` (krótki tap),
//   engine.dispose()             — zwolnij też model (WebWorker).
//
// Twardość na Androida (regresja z tabletu, 2026-09-09 — pusty transkrypt mimo
// działającego PTT):
//   • `await context.resume()` — `AudioContext` startuje `suspended` (polityka
//     autoplay) i `ScriptProcessorNode` dostaje wtedy bufory zerowe,
//   • strażnik `stream.getAudioTracks()[0].muted` — `getUserMedia` bywa
//     rozwiązane mimo martwego wejścia (inna apka / cofnięte uprawnienie),
//   • watchdog RMS — jeśli przez `silenceTimeoutMs` nie przyjdzie ani jedna
//     niezerowa próbka, sesja kończy się `onError`, a nie cichą pustą notatką,
//   • WYMUSZONE 16 kHz (2026-09-09, druga fala regresji z tabletu — audio płynie,
//     `onPartial` leci, ale wynik jest pusty) — `AudioContext({ sampleRate:
//     16000 })`, a gdy mobilny Chrome / WebView opcję zignoruje i i tak da np.
//     48 kHz, ręczne zejście do 16 kHz (`downsampleMono`) przed
//     `acceptWaveformFloat`. Mały model Vosk (Kaldi) NIE ma resamplera —
//     niezgodny sample rate = poprawny sygnał, rozjechane cechy MFCC, `""`.

import type { SpeechDiagnosticEvent, SpeechEngine, SpeechEngineCallbacks } from './SpeechEngine'
import { checkpoint, logError, logWarn } from '../debug/AppDebugLog'

/** Kształt komunikatu `result` z rozpoznawania (pełny segment wypowiedzi). */
export interface VoskResultMessage {
  result: { text: string }
}

/** Kształt komunikatu `partialresult` (wynik częściowy bieżącej wypowiedzi). */
export interface VoskPartialResultMessage {
  result: { partial: string }
}

/** Kształt komunikatu `error` z warstwy `vosk-browser`. */
export interface VoskErrorMessage {
  error: string
}

/** Wąski widok na `KaldiRecognizer` z `vosk-browser` — tylko to, czego używamy. */
export interface VoskRecognizerLike {
  on(event: 'result', listener: (message: VoskResultMessage) => void): void
  on(event: 'partialresult', listener: (message: VoskPartialResultMessage) => void): void
  on(event: 'error', listener: (message: VoskErrorMessage) => void): void
  acceptWaveform(buffer: AudioBuffer): void
  /**
   * Ścieżka dla ręcznie zresamplowanego PCM — omija `buffer.sampleRate` z
   * `AudioBuffer` i deklaruje częstotliwość wprost. Używana, gdy `AudioContext`
   * zignorował żądane 16 kHz i musimy zejść na dół sami (mobilny Chrome / WebView).
   */
  acceptWaveformFloat(buffer: Float32Array, sampleRate: number): void
  retrieveFinalResult(): void
  remove(): void
}

/** Wąski widok na `Model` z `vosk-browser`. */
export interface VoskModelLike {
  KaldiRecognizer: new (sampleRate: number, grammar?: string) => VoskRecognizerLike
  terminate(): void
}

/** Fabryka modelu — domyślnie leniwy `import('vosk-browser').createModel`. */
export type CreateModelFn = (modelUrl: string, logLevel?: number) => Promise<VoskModelLike>

/** Callbacki sesji rozpoznawania — wspólny kontrakt {@link SpeechEngineCallbacks}. */
export type VoskSpeechCallbacks = SpeechEngineCallbacks

export interface VoskSpeechEngineOptions {
  /** URL archiwum `.tar.gz` z modelem (np. `vosk-model-small-pl-0.22`). */
  modelUrl: string
  /** Fabryka modelu. Domyślnie leniwy `import('vosk-browser')`. */
  createModel?: CreateModelFn
  /** Dostęp do mikrofonu. Domyślnie `navigator.mediaDevices.getUserMedia`. */
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>
  /** Fabryka `AudioContext`. Domyślnie `window.AudioContext` / `webkitAudioContext`. */
  createAudioContext?: () => AudioContext
  /** Poziom logów `vosk-browser` (−1 = cisza). Domyślnie −1. */
  logLevel?: number
  /** Ile ms czekać na wypłukany wynik po `stop()`, zanim oddamy to, co mamy. */
  flushTimeoutMs?: number
  /** Planowanie odroczonego zadania. Domyślnie `setTimeout`. */
  scheduleTimeout?: (callback: () => void, ms: number) => ReturnType<typeof setTimeout>
  /** Anulowanie odroczonego zadania. Domyślnie `clearTimeout`. */
  cancelTimeout?: (handle: ReturnType<typeof setTimeout>) => void
  /**
   * Ile ms samej ciszy systemowej (RMS = 0) toleruje watchdog po `start()`,
   * zanim uzna tor audio za martwy i zgłosi błąd. Domyślnie 1500.
   */
  silenceTimeoutMs?: number
  /** Planowanie watchdogu ciszy. Domyślnie `setTimeout` (osobny od `scheduleTimeout`). */
  scheduleSilenceWatchdog?: (callback: () => void, ms: number) => ReturnType<typeof setTimeout>
  /** Anulowanie watchdogu ciszy. Domyślnie `clearTimeout`. */
  cancelSilenceWatchdog?: (handle: ReturnType<typeof setTimeout>) => void
  /**
   * Krok 3 (FAZA 1) — gramatyka Vosk: lista fraz o bounded słownictwie hali
   * (`production_dictionaries.ts`). Podana i niepusta ⇒ recognizer powstaje jako
   * `new KaldiRecognizer(rate, JSON.stringify([...grammar, '[unk]']))` (zamknięty
   * słownik + token nieznanego słowa) — duży skok trafności w hałasie dla
   * krótkich notatek, kosztem swobodnej wypowiedzi. Pominięta ⇒ otwarty słownik.
   * Gdy build modelu nie przyjmie gramatyki (konstruktor rzuci) — silnik sam
   * degraduje do otwartego słownika i zgłasza to przez `onDiagnostic`
   * (`{ type: 'grammar', fellBackToOpen: true }`).
   */
  grammar?: readonly string[]
  /**
   * Sink telemetrii diagnostycznej (krok 7 walidacji w terenie). Domyślnie
   * no-op — bez podpiętego odbiorcy silnik zachowuje się identycznie. NIGDY nie
   * wywróci sesji: wyjątki z sinka są łykane.
   */
  onDiagnostic?: (event: SpeechDiagnosticEvent) => void
  /** Zegar do pomiaru `elapsedMs` w zdarzeniach diagnostycznych. Domyślnie `Date.now`. */
  now?: () => number
}

/** Domyślny rozmiar bufora `ScriptProcessorNode` — jak w przykładach `vosk-browser`. */
const SCRIPT_PROCESSOR_BUFFER = 4096
const DEFAULT_FLUSH_TIMEOUT_MS = 400
/** Domyślny budżet ciszy dla watchdogu RMS (patrz `silenceTimeoutMs`). */
const DEFAULT_SILENCE_TIMEOUT_MS = 1500
/**
 * Częstotliwość, pod którą zbudowany jest model `vosk-model-small-pl-0.22` (jak
 * każdy mały model Vosk/Kaldi). Ten build `vosk-browser` NIE ma wewnętrznego
 * resamplera — audio o innej częstotliwości daje rozjechane cechy MFCC i pusty
 * wynik. Wymuszamy 16 kHz na `AudioContext`, a jeśli przeglądarka opcję zignoruje
 * (typowo mobilny Chrome / WebView) — schodzimy do 16 kHz ręcznie tuż przed
 * podaniem próbek do rozpoznawania.
 */
const TARGET_SAMPLE_RATE = 16000

async function defaultCreateModel(modelUrl: string, logLevel?: number): Promise<VoskModelLike> {
  // Leniwy import — `vosk-browser` to ~5,8 MB (WASM w base64). Ładujemy go
  // dopiero, gdy ktoś faktycznie uruchamia dyktowanie offline.
  const mod = (await import('vosk-browser')) as unknown as {
    createModel: (url: string, level?: number) => Promise<VoskModelLike>
  }
  return mod.createModel(modelUrl, logLevel)
}

function defaultGetUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia(constraints)
}

function defaultCreateAudioContext(): AudioContext {
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) throw new Error('AudioContext niedostępny')
  try {
    // Preferowana ścieżka: kontekst od razu w 16 kHz — wtedy `ScriptProcessorNode`
    // oddaje bufory 16 kHz i karmimy Vosk bez resamplingu.
    return new Ctor({ sampleRate: TARGET_SAMPLE_RATE })
  } catch {
    // Starsze Safari / egzotyczne WebView potrafią rzucić na nieobsługiwanym
    // `sampleRate` w konstruktorze — bierzemy kontekst domyślny, a zejście do
    // 16 kHz robi wtedy `downsampleMono` w torze audio.
    return new Ctor()
  }
}

function describeError(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return `${fallback}: ${error.message}`
  if (typeof error === 'string' && error) return `${fallback}: ${error}`
  return fallback
}

/**
 * Watchdog ciszy: `true`, gdy w kanale 0 bufora jest choć jedna niezerowa
 * próbka. Równoważne „RMS > 0" (RMS = 0 ⇔ same zera), ale bez dzielenia / `sqrt`
 * i bez ryzyka `NaN` dla pustego bufora. Zerowy sygnał utrzymujący się przez
 * `silenceTimeoutMs` = martwy / wyciszony tor audio (brak Secure Context,
 * cofnięte uprawnienie do mikrofonu, inna aplikacja trzyma mikrofon,
 * `AudioContext` nie wznowiony).
 */
function bufferHasSignal(buffer: AudioBuffer): boolean {
  let samples: Float32Array
  try {
    samples = buffer.getChannelData(0)
  } catch {
    // Bufor bez `getChannelData` (atrapa / egzotyczny WebView) — traktuj jak brak sygnału.
    return false
  }
  for (let i = 0; i < samples.length; i += 1) {
    if (samples[i] !== 0) return true
  }
  return false
}

/**
 * Zejście `fromRate → toRate` (wyłącznie w dół — dla `toRate >= fromRate` zwraca
 * wejście bez zmian) metodą interpolacji liniowej. To nie jest studyjny resampler
 * — nie ma filtra antyaliasingowego z prawdziwego zdarzenia — ale `getUserMedia`
 * z włączonym `noiseSuppression` / `echoCancellation` pasmo i tak ogranicza, a
 * realny problem nie jest aliasing, tylko błędnie zadeklarowana częstotliwość
 * (model Vosk sam nie resample'uje). Tani, sprawdzony wzorzec z ekosystemu
 * WebRTC/Vosk; 48 kHz → 16 kHz w zupełności wystarcza do rozpoznania mowy.
 */
export function downsampleMono(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (toRate >= fromRate) return input
  const ratio = fromRate / toRate
  const outLength = Math.ceil(input.length / ratio)
  const output = new Float32Array(outLength)
  for (let i = 0; i < outLength; i += 1) {
    const position = i * ratio
    const left = Math.floor(position)
    const right = left + 1 < input.length ? left + 1 : left
    output[i] = input[left] + (input[right] - input[left]) * (position - left)
  }
  return output
}

export class VoskSpeechEngine implements SpeechEngine {
  private readonly modelUrl: string
  private readonly createModel: CreateModelFn
  private readonly getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>
  private readonly createAudioContext: () => AudioContext
  private readonly logLevel: number
  private readonly flushTimeoutMs: number
  private readonly scheduleTimeout: (callback: () => void, ms: number) => ReturnType<typeof setTimeout>
  private readonly cancelTimeout: (handle: ReturnType<typeof setTimeout>) => void
  private readonly silenceTimeoutMs: number
  private readonly scheduleSilenceWatchdog: (
    callback: () => void,
    ms: number,
  ) => ReturnType<typeof setTimeout>
  private readonly cancelSilenceWatchdog: (handle: ReturnType<typeof setTimeout>) => void
  private readonly grammar: readonly string[] | null
  private readonly onDiagnostic: (event: SpeechDiagnosticEvent) => void
  private readonly now: () => number

  private model: VoskModelLike | null = null
  private loadPromise: Promise<void> | null = null

  private recognizer: VoskRecognizerLike | null = null
  private context: AudioContext | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private processor: ScriptProcessorNode | null = null
  private stream: MediaStream | null = null
  // Faktyczna (zaokrąglona) częstotliwość `AudioContext` oraz częstotliwość, z
  // jaką powstał recognizer. Równe → karmimy `AudioBuffer` wprost. Różne
  // (przeglądarka zignorowała `sampleRate: 16000`) → downsampling do 16 kHz.
  private inputSampleRate = TARGET_SAMPLE_RATE
  private recognizerSampleRate = TARGET_SAMPLE_RATE

  private callbacks: VoskSpeechCallbacks | null = null
  private accumulated = ''
  private active = false
  private recording = false
  private flushing = false
  private finalDelivered = false
  private flushTimer: ReturnType<typeof setTimeout> | null = null
  // Watchdog ciszy: uzbrojony w `start()`, rozbrajany pierwszą niezerową ramką
  // audio lub `stop()` / `cancel()` / teardownem. Gdy wystrzeli — tor audio jest
  // martwy i leci `onError` (terminalnie, jak błąd recognizera).
  private silenceTimer: ReturnType<typeof setTimeout> | null = null
  private sawAudioSignal = false
  // Diagnostyka (krok 7): znacznik wejścia w `start()` i strażniki „raz na sesję".
  private sessionStartedAt = 0
  private firstAudioReported = false
  private firstPartialReported = false

  constructor(options: VoskSpeechEngineOptions) {
    this.modelUrl = options.modelUrl
    this.createModel = options.createModel ?? defaultCreateModel
    this.getUserMedia = options.getUserMedia ?? defaultGetUserMedia
    this.createAudioContext = options.createAudioContext ?? defaultCreateAudioContext
    this.logLevel = options.logLevel ?? -1
    this.flushTimeoutMs = options.flushTimeoutMs ?? DEFAULT_FLUSH_TIMEOUT_MS
    this.scheduleTimeout = options.scheduleTimeout ?? ((cb, ms) => setTimeout(cb, ms))
    this.cancelTimeout = options.cancelTimeout ?? ((handle) => clearTimeout(handle))
    this.silenceTimeoutMs = options.silenceTimeoutMs ?? DEFAULT_SILENCE_TIMEOUT_MS
    this.scheduleSilenceWatchdog = options.scheduleSilenceWatchdog ?? ((cb, ms) => setTimeout(cb, ms))
    this.cancelSilenceWatchdog = options.cancelSilenceWatchdog ?? ((handle) => clearTimeout(handle))
    this.grammar = options.grammar && options.grammar.length > 0 ? options.grammar : null
    this.onDiagnostic = options.onDiagnostic ?? (() => { })
    this.now = options.now ?? (() => Date.now())
  }

  /** Wysyła zdarzenie diagnostyczne. Wyjątki z sinka NIGDY nie wywracają sesji. */
  private emitDiagnostic(event: SpeechDiagnosticEvent): void {
    try {
      this.onDiagnostic(event)
    } catch {
      // sink diagnostyczny nie może zaszkodzić dyktowaniu — świadomie łykane
    }
  }

  /** Ms od wejścia w `start()` — do `elapsedMs` w zdarzeniach diagnostycznych. */
  private elapsed(): number {
    return Math.round(this.now() - this.sessionStartedAt)
  }

  /** „Pierwszy `partialresult`" (krok 7) — raz na sesję, z partiala LUB segmentu. */
  private reportFirstPartial(text: string): void {
    if (this.firstPartialReported || !text) return
    this.firstPartialReported = true
    this.emitDiagnostic({ type: 'first-partial', elapsedMs: this.elapsed(), text })
  }

  /**
   * Buduje `KaldiRecognizer`. Bez gramatyki — otwarty słownik. Z gramatyką —
   * zamknięty słownik + token `[unk]`; gdy ten build modelu jej nie przyjmie
   * (konstruktor rzuci), degradujemy do otwartego słownika, żeby dyktowanie
   * offline w ogóle ruszyło. Wybór ścieżki idzie w telemetrię (`grammar`).
   */
  private createRecognizer(model: VoskModelLike): VoskRecognizerLike {
    const grammar = this.grammar
    if (grammar) {
      try {
        const recognizer = new model.KaldiRecognizer(
          this.recognizerSampleRate,
          JSON.stringify([...grammar, '[unk]']),
        )
        this.emitDiagnostic({
          type: 'grammar',
          mode: 'closed',
          wordCount: grammar.length,
          fellBackToOpen: false,
        })
        return recognizer
      } catch {
        const recognizer = new model.KaldiRecognizer(this.recognizerSampleRate)
        this.emitDiagnostic({
          type: 'grammar',
          mode: 'open',
          wordCount: grammar.length,
          fellBackToOpen: true,
        })
        return recognizer
      }
    }
    const recognizer = new model.KaldiRecognizer(this.recognizerSampleRate)
    this.emitDiagnostic({ type: 'grammar', mode: 'open', wordCount: 0, fellBackToOpen: false })
    return recognizer
  }

  /** Czy model jest już wczytany i gotowy do `start()` bez sieci. */
  get ready(): boolean {
    return this.model !== null
  }

  /**
   * Pobiera / inicjuje model. Idempotentne: kolejne wywołania to no-op, a
   * równoległe współdzielą jedną obietnicę. Rzuca, jeśli modelu nie da się wczytać.
   */
  async load(): Promise<void> {
    if (this.model) {
      checkpoint('VOSK', 'model already loaded')
      return
    }
    if (this.loadPromise) {
      checkpoint('VOSK', 'model load in progress, waiting')
      return this.loadPromise
    }
    checkpoint('VOSK', 'model load started', { modelUrl: this.modelUrl })
    this.loadPromise = (async () => {
      this.model = await this.createModel(this.modelUrl, this.logLevel)
    })()
    try {
      await this.loadPromise
      checkpoint('VOSK', 'model load success')
    } catch (error) {
      logError('VOSK', 'model load failed', error)
      throw error
    } finally {
      this.loadPromise = null
    }
  }

  /** {@link SpeechEngine.preload} — pobiera model z góry (delegat do `load()`). */
  preload(): Promise<void> {
    return this.load()
  }

  /**
   * Otwiera mikrofon i rozpoczyna rozpoznawanie. `onPartial` leci na żywo.
   * Błędy (model / mikrofon / audio) idą przez `callbacks.onError` — `start`
   * NIGDY nie rzuca, żeby nie wywrócić UI wywołującego.
   */
  async start(callbacks: VoskSpeechCallbacks): Promise<void> {
    this.callbacks = callbacks
    this.accumulated = ''
    this.flushing = false
    this.finalDelivered = false
    this.sawAudioSignal = false
    this.sessionStartedAt = this.now()
    this.firstAudioReported = false
    this.firstPartialReported = false
    checkpoint('VOSK', 'start called')

    try {
      await this.load()
    } catch (error) {
      logError('VOSK', 'start failed at model load', error)
      callbacks.onError?.(describeError(error, 'Nie udało się wczytać modelu offline'))
      return
    }
    const model = this.model
    if (!model) {
      logError('VOSK', 'start failed: model null after load')
      callbacks.onError?.('Model offline niedostępny')
      return
    }
    checkpoint('VOSK', 'model ready', { elapsedMs: this.elapsed() })
    this.emitDiagnostic({ type: 'model-loaded', elapsedMs: this.elapsed() })

    let stream: MediaStream
    checkpoint('VOSK', 'requesting microphone')
    try {
      stream = await this.getUserMedia({
        // Dla małego modelu Vosk zostawiamy filtry przeglądarki WŁĄCZONE —
        // pomagają w hałasie hali (inaczej niż tor Deepgram, gdzie je gasiliśmy).
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      })
    } catch (error) {
      logError('VOSK', 'microphone permission denied', error)
      callbacks.onError?.(describeError(error, 'Brak dostępu do mikrofonu'))
      return
    }
    this.stream = stream
    checkpoint('VOSK', 'microphone stream acquired', { tracks: stream.getAudioTracks().length })

    // Strażnik strumienia: `getUserMedia` potrafi ROZWIĄZAĆ się mimo martwego
    // wejścia — brak toru audio albo tor od razu `muted` (inna apka trzyma
    // mikrofon, cofnięte uprawnienie systemowe). Wtedy nie ma po co budować grafu.
    const audioTrack = stream.getAudioTracks()[0]
    if (!audioTrack) {
      logError('VOSK', 'no audio track in stream')
      this.teardownSession()
      callbacks.onError?.('Brak toru audio w strumieniu mikrofonu')
      return
    }
    if (audioTrack.muted) {
      logWarn('VOSK', 'audio track muted by system')
      this.teardownSession()
      callbacks.onError?.('Mikrofon jest wyciszony przez system (sprawdź uprawnienia lub HTTPS)')
      return
    }

    try {
      const context = this.createAudioContext()
      this.context = context
      // Android/iOS tworzą `AudioContext` w stanie `suspended` (polityka
      // autoplay). Bez `resume()` `ScriptProcessorNode.onaudioprocess` dostaje
      // bufory zerowe — mikrofon „nagrywa ciszę". `resume()` bywa odrzucane, gdy
      // kontekst już działa albo start jest poza gestem użytkownika — to NIE jest
      // błąd krytyczny; realną, utrzymującą się ciszę wyłapie watchdog RMS niżej.
      try {
        await context.resume()
      } catch {
        // świadomie łykane — patrz komentarz wyżej
      }
      this.inputSampleRate = Math.round(context.sampleRate)
      // Recognizer zawsze dostaje 16 kHz: albo natywnie (kontekst uszanował
      // żądanie), albo po `downsampleMono`. Wyjątek — egzotyczny kontekst poniżej
      // 16 kHz: nie „domalowujemy" próbek, bierzemy jego częstotliwość wprost.
      this.recognizerSampleRate =
        this.inputSampleRate > TARGET_SAMPLE_RATE ? TARGET_SAMPLE_RATE : this.inputSampleRate
      const recognizer = this.createRecognizer(model)
      this.recognizer = recognizer
      recognizer.on('result', this.handleResult)
      recognizer.on('partialresult', this.handlePartialResult)
      recognizer.on('error', this.handleRecognizerError)
      checkpoint('VOSK', 'audio context ready', {
        inputSampleRate: this.inputSampleRate,
        recognizerSampleRate: this.recognizerSampleRate,
        downsampling: this.inputSampleRate !== this.recognizerSampleRate,
        elapsedMs: this.elapsed(),
      })
      this.emitDiagnostic({
        type: 'context-ready',
        inputSampleRate: this.inputSampleRate,
        recognizerSampleRate: this.recognizerSampleRate,
        downsampling: this.inputSampleRate !== this.recognizerSampleRate,
        elapsedMs: this.elapsed(),
      })

      this.source = context.createMediaStreamSource(stream)
      this.processor = context.createScriptProcessor(SCRIPT_PROCESSOR_BUFFER, 1, 1)
      this.processor.onaudioprocess = this.handleAudioProcess
      this.source.connect(this.processor)
      // Wyjście procesora nie jest zapisywane (zostaje ciszą) — podłączenie do
      // `destination` jedynie pompuje `onaudioprocess`, nie słychać mikrofonu.
      this.processor.connect(context.destination)
    } catch (error) {
      logError('VOSK', 'audio context setup failed', error)
      this.teardownSession()
      callbacks.onError?.(describeError(error, 'Nie udało się uruchomić przechwytywania audio'))
      return
    }

    this.active = true
    this.recording = true
    checkpoint('VOSK', 'recording active')
    // Uzbrój watchdog ciszy — rozbroi go pierwsza niezerowa ramka w `handleAudioProcess`.
    this.silenceTimer = this.scheduleSilenceWatchdog(this.handleSilenceWatchdog, this.silenceTimeoutMs)
  }

  /**
   * Domyka wypowiedź: przestaje karmić audio, wymusza wypłukanie ostatniego
   * wyniku i oddaje pełny transkrypt przez `onFinal`. Bezpiecznik czasowy
   * gwarantuje `onFinal` nawet, gdy `vosk-browser` nie odeśle `result`.
   */
  stop(): void {
    if (!this.active) return
    this.active = false
    this.recording = false
    // Użytkownik puścił przycisk — watchdog ciszy nie ma już nic do pilnowania
    // (a błąd „brak dźwięku" w trakcie płukania tylko by mylił).
    this.clearSilenceWatchdog()

    const recognizer = this.recognizer
    if (!recognizer) {
      this.deliverFinal()
      return
    }
    this.flushing = true
    try {
      recognizer.retrieveFinalResult()
    } catch {
      // `retrieveFinalResult` padło — oddajemy to, co już zebraliśmy.
      this.deliverFinal()
      return
    }
    // `retrieveFinalResult` mogło odesłać `result` SYNCHRONICZNIE (atrapa/szybki
    // worker) — wtedy `onFinal` już poszło i nie ma po co uzbrajać bezpiecznika.
    if (this.finalDelivered) return
    // Jedyny punkt sprzątania to `deliverFinal` — on sam skasuje ten uchwyt.
    this.flushTimer = this.scheduleTimeout(() => this.deliverFinal(), this.flushTimeoutMs)
  }

  /** Przerywa sesję BEZ `onFinal` (krótki tap / rezygnacja). Idempotentne. */
  cancel(): void {
    if (!this.active && !this.recording && !this.flushing) return
    this.active = false
    this.recording = false
    this.flushing = false
    this.finalDelivered = true
    if (this.flushTimer !== null) {
      this.cancelTimeout(this.flushTimer)
      this.flushTimer = null
    }
    this.teardownSession()
  }

  /** Zwalnia sesję ORAZ model (WebWorker `vosk-browser`). Po tym trzeba `load()`. */
  dispose(): void {
    this.cancel()
    const model = this.model
    this.model = null
    if (model) {
      try {
        model.terminate()
      } catch {
        // worker mógł być już zamknięty — nieistotne
      }
    }
  }

  private readonly handleAudioProcess = (event: AudioProcessingEvent): void => {
    const recognizer = this.recognizer
    if (!this.recording || !recognizer) return
    this.feedAudio(recognizer, event.inputBuffer)
    // Pierwsza ramka z realnym sygnałem rozbraja watchdog ciszy — mikrofon żyje.
    if (!this.sawAudioSignal && bufferHasSignal(event.inputBuffer)) {
      this.sawAudioSignal = true
      this.clearSilenceWatchdog()
      if (!this.firstAudioReported) {
        this.firstAudioReported = true
        checkpoint('VOSK', 'first audio signal received', { elapsedMs: this.elapsed() })
        this.emitDiagnostic({ type: 'first-audio-signal', elapsedMs: this.elapsed() })
      }
    }
  }

  /**
   * Podaje ramkę PCM do rozpoznawania z GWARANCJĄ 16 kHz. Gdy `AudioContext`
   * uszanował żądane 16 kHz — karmimy `AudioBuffer` wprost (`vosk-browser` czyta
   * `buffer.sampleRate`). Gdy je zignorował (mobilny Chrome / WebView potrafią
   * uparcie dać 48 kHz) — schodzimy `downsampleMono` i idziemy
   * `acceptWaveformFloat`, bo model Vosk sam nie resample'uje i przy 48 kHz
   * zwróciłby pusty transkrypt.
   */
  private feedAudio(recognizer: VoskRecognizerLike, inputBuffer: AudioBuffer): void {
    if (this.inputSampleRate === this.recognizerSampleRate) {
      recognizer.acceptWaveform(inputBuffer)
      return
    }
    let mono: Float32Array
    try {
      mono = inputBuffer.getChannelData(0)
    } catch {
      // Bufor bez `getChannelData` (atrapa / egzotyczny WebView) — nie ma czego karmić.
      return
    }
    recognizer.acceptWaveformFloat(
      downsampleMono(mono, this.inputSampleRate, this.recognizerSampleRate),
      this.recognizerSampleRate,
    )
  }

  /**
   * Watchdog ciszy wystrzelił: przez `silenceTimeoutMs` nie przyszła ani jedna
   * niezerowa próbka. Tor audio jest martwy — kończymy sesję terminalnie (jak
   * przy błędzie recognizera: po tym NIE ma `onFinal`) i mówimy to wprost.
   */
  private readonly handleSilenceWatchdog = (): void => {
    this.silenceTimer = null
    if (!this.active || this.sawAudioSignal) return
    logWarn('VOSK', 'silence watchdog triggered', { elapsedMs: this.elapsed() })
    const callbacks = this.callbacks
    this.active = false
    this.recording = false
    this.finalDelivered = true
    this.emitDiagnostic({ type: 'silence-timeout', elapsedMs: this.elapsed() })
    this.teardownSession()
    callbacks?.onError?.('Mikrofon nie przechwytuje dźwięku (sprawdź uprawnienia lub HTTPS)')
  }

  private clearSilenceWatchdog(): void {
    if (this.silenceTimer !== null) {
      this.cancelSilenceWatchdog(this.silenceTimer)
      this.silenceTimer = null
    }
  }

  private readonly handleResult = (message: VoskResultMessage): void => {
    const text = (message.result?.text ?? '').trim()
    if (text) {
      checkpoint('VOSK', 'result segment', { text, accumulated: this.accumulated, elapsedMs: this.elapsed() })
      this.reportFirstPartial(text)
      this.accumulated = this.accumulated ? `${this.accumulated} ${text}` : text
      if (!this.flushing) {
        this.callbacks?.onPartial?.(this.accumulated)
        // Segment domknięty przez Vosk (przerwa w mówieniu) — jeszcze w trakcie sesji.
        this.callbacks?.onSegmentFinal?.(text, this.accumulated)
      }
    }
    if (this.flushing) this.deliverFinal()
  }

  private readonly handlePartialResult = (message: VoskPartialResultMessage): void => {
    if (this.flushing) return
    const partial = (message.result?.partial ?? '').trim()
    if (partial && !this.firstPartialReported) {
      checkpoint('VOSK', 'first partial', { partial, elapsedMs: this.elapsed() })
    }
    this.reportFirstPartial(partial)
    const live = partial
      ? this.accumulated
        ? `${this.accumulated} ${partial}`
        : partial
      : this.accumulated
    this.callbacks?.onPartial?.(live)
  }

  private readonly handleRecognizerError = (message: VoskErrorMessage): void => {
    logError('VOSK', 'recognizer error', { error: message?.error })
    this.callbacks?.onError?.(describeError(message?.error, 'Błąd rozpoznawania offline'))
  }

  private deliverFinal(): void {
    if (this.finalDelivered) return
    this.finalDelivered = true
    if (this.flushTimer !== null) {
      this.cancelTimeout(this.flushTimer)
      this.flushTimer = null
    }
    const text = this.accumulated.trim()
    checkpoint('VOSK', 'final delivered', { text, chars: text.length, elapsedMs: this.elapsed() })
    const callbacks = this.callbacks
    this.emitDiagnostic({ type: 'final', elapsedMs: this.elapsed(), chars: text.length })
    this.teardownSession()
    callbacks?.onFinal(text)
  }

  /** Odpina graf audio i zwalnia mikrofon. Model ZOSTAJE. Idempotentne. */
  private teardownSession(): void {
    this.flushing = false
    this.clearSilenceWatchdog()
    if (this.processor) {
      this.processor.onaudioprocess = null
      try {
        this.processor.disconnect()
      } catch {
        // mogło być już odpięte
      }
      this.processor = null
    }
    if (this.source) {
      try {
        this.source.disconnect()
      } catch {
        // mogło być już odpięte
      }
      this.source = null
    }
    if (this.recognizer) {
      try {
        this.recognizer.remove()
      } catch {
        // recognizer mógł być już usunięty
      }
      this.recognizer = null
    }
    const context = this.context
    this.context = null
    if (context) {
      try {
        void context.close()
      } catch {
        // AudioContext mógł być już zamknięty
      }
    }
    if (this.stream) {
      for (const track of this.stream.getTracks()) {
        try {
          track.stop()
        } catch {
          // ścieżka mogła być już zatrzymana
        }
      }
      this.stream = null
    }
  }
}
